import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Tab, Nav, Button } from 'react-bootstrap';
import apiService from '../../services/api';
import InfrastructureSectionPanel from './InfrastructureSectionPanel';
import InfrastructureHealthPanel from './InfrastructureHealthPanel';
import InfrastructureRuntimePanel from './InfrastructureRuntimePanel';
import InfrastructureMemoryPanel from './InfrastructureMemoryPanel';
import InfrastructureCpuPanel from './InfrastructureCpuPanel';
import InfrastructureOsPanel from './InfrastructureOsPanel';
import InfrastructureNetworkPanel from './InfrastructureNetworkPanel';
import InfrastructureDatabasePanel from './InfrastructureDatabasePanel';
import InfrastructureWebappPanel from './InfrastructureWebappPanel';

const SECTIONS = [
    { key: 'api', label: 'API', icon: 'bi-braces', description: 'Health, runtime, memory, CPU, OS & network metrics' },
    { key: 'database', label: 'Database', icon: 'bi-database', description: 'Connection pools, query performance & storage stats' },
    { key: 'webapp', label: 'Web App', icon: 'bi-globe', description: 'Front-end availability, response times & errors' },
    { key: 'cloud', label: 'Cloud Environment', icon: 'bi-cloud-fill', description: 'Cloud resource usage, scaling & environment health' },
];

const ApiDetail = () => {
    const [activeTab, setActiveTab] = useState('health');

    const [health, setHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [healthError, setHealthError] = useState('');

    const [runtime, setRuntime] = useState(null);
    const [runtimeLoading, setRuntimeLoading] = useState(false);
    const [runtimeError, setRuntimeError] = useState('');

    const [memory, setMemory] = useState(null);
    const [memoryLoading, setMemoryLoading] = useState(false);
    const [memoryError, setMemoryError] = useState('');

    const [cpu, setCpu] = useState(null);
    const [cpuLoading, setCpuLoading] = useState(false);
    const [cpuError, setCpuError] = useState('');

    const [os, setOs] = useState(null);
    const [osLoading, setOsLoading] = useState(false);
    const [osError, setOsError] = useState('');

    const [network, setNetwork] = useState(null);
    const [networkLoading, setNetworkLoading] = useState(false);
    const [networkError, setNetworkError] = useState('');

    const fetchHealth = useCallback(async () => {
        setHealthLoading(true);
        setHealthError('');
        const res = await apiService.getTechnicalHealth();
        if (res.success) setHealth(res.data);
        else setHealthError(res.error || 'Failed to load health data');
        setHealthLoading(false);
    }, []);

    const fetchRuntime = useCallback(async () => {
        setRuntimeLoading(true);
        setRuntimeError('');
        const res = await apiService.getTechnicalRuntime();
        if (res.success) setRuntime(res.data);
        else setRuntimeError(res.error || 'Failed to load runtime data');
        setRuntimeLoading(false);
    }, []);

    const fetchMemory = useCallback(async () => {
        setMemoryLoading(true);
        setMemoryError('');
        const res = await apiService.getTechnicalMemory();
        if (res.success) setMemory(res.data);
        else setMemoryError(res.error || 'Failed to load memory data');
        setMemoryLoading(false);
    }, []);

    const fetchCpu = useCallback(async () => {
        setCpuLoading(true);
        setCpuError('');
        const res = await apiService.getTechnicalCpu();
        if (res.success) setCpu(res.data);
        else setCpuError(res.error || 'Failed to load CPU data');
        setCpuLoading(false);
    }, []);

    const fetchOs = useCallback(async () => {
        setOsLoading(true);
        setOsError('');
        const res = await apiService.getTechnicalOs();
        if (res.success) setOs(res.data);
        else setOsError(res.error || 'Failed to load OS data');
        setOsLoading(false);
    }, []);

    const fetchNetwork = useCallback(async () => {
        setNetworkLoading(true);
        setNetworkError('');
        const res = await apiService.getTechnicalNetwork();
        if (res.success) setNetwork(res.data);
        else setNetworkError(res.error || 'Failed to load network data');
        setNetworkLoading(false);
    }, []);

    useEffect(() => { fetchHealth(); }, [fetchHealth]);

    const refreshMap = {
        health: fetchHealth,
        runtime: fetchRuntime,
        memory: fetchMemory,
        cpu: fetchCpu,
        os: fetchOs,
        network: fetchNetwork,
    };

    const handleTabSelect = (key) => {
        setActiveTab(key);
        if (key === 'runtime' && !runtime && !runtimeLoading) fetchRuntime();
        if (key === 'memory' && !memory && !memoryLoading) fetchMemory();
        if (key === 'cpu' && !cpu && !cpuLoading) fetchCpu();
        if (key === 'os' && !os && !osLoading) fetchOs();
        if (key === 'network' && !network && !networkLoading) fetchNetwork();
    };

    const anyLoading = healthLoading || runtimeLoading || memoryLoading || cpuLoading || osLoading || networkLoading;

    return (
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0"><i className="bi bi-braces me-2"></i>API</h5>
                <Button variant="outline-secondary" size="sm" onClick={refreshMap[activeTab]} disabled={anyLoading}>
                    <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </Button>
            </Card.Header>
            <Card.Body className="p-0">
                <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
                    <Nav variant="tabs" className="px-3 pt-2 border-bottom">
                        <Nav.Item><Nav.Link eventKey="health"><i className="bi bi-heart-pulse me-1"></i>Health</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="runtime"><i className="bi bi-terminal me-1"></i>Runtime</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="memory"><i className="bi bi-memory me-1"></i>Memory</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="cpu"><i className="bi bi-cpu me-1"></i>CPU</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="os"><i className="bi bi-pc-display me-1"></i>OS</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="network"><i className="bi bi-diagram-3 me-1"></i>Network / HTTP</Nav.Link></Nav.Item>
                    </Nav>
                    <Tab.Content className="p-3">
                        <Tab.Pane eventKey="health">
                            <InfrastructureSectionPanel loading={healthLoading} error={healthError} onRefresh={fetchHealth}>
                                {health && <InfrastructureHealthPanel data={health} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                        <Tab.Pane eventKey="runtime">
                            <InfrastructureSectionPanel loading={runtimeLoading} error={runtimeError} onRefresh={fetchRuntime}>
                                {runtime && <InfrastructureRuntimePanel data={runtime} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                        <Tab.Pane eventKey="memory">
                            <InfrastructureSectionPanel loading={memoryLoading} error={memoryError} onRefresh={fetchMemory}>
                                {memory && <InfrastructureMemoryPanel data={memory} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                        <Tab.Pane eventKey="cpu">
                            <InfrastructureSectionPanel loading={cpuLoading} error={cpuError} onRefresh={fetchCpu}>
                                {cpu && <InfrastructureCpuPanel data={cpu} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                        <Tab.Pane eventKey="os">
                            <InfrastructureSectionPanel loading={osLoading} error={osError} onRefresh={fetchOs}>
                                {os && <InfrastructureOsPanel data={os} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                        <Tab.Pane eventKey="network">
                            <InfrastructureSectionPanel loading={networkLoading} error={networkError} onRefresh={fetchNetwork}>
                                {network && <InfrastructureNetworkPanel data={network} />}
                            </InfrastructureSectionPanel>
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </Card.Body>
        </Card>
    );
};

const InfrastructureStatus = () => {
    const [activeSection, setActiveSection] = useState(null);

    const activeConfig = SECTIONS.find(s => s.key === activeSection);

    if (activeSection) {
        return (
            <Container fluid className="p-4">
                <Row className="mb-4">
                    <Col>
                        <Button variant="link" className="p-0 text-decoration-none mb-2" onClick={() => setActiveSection(null)}>
                            <i className="bi bi-arrow-left me-1"></i>Back to Infrastructure
                        </Button>
                        <h2>
                            <i className={`bi ${activeConfig.icon} me-2`}></i>
                            {activeConfig.label}
                        </h2>
                        <p className="text-muted">{activeConfig.description}</p>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {activeSection === 'api' && <ApiDetail />}
                        {activeSection === 'database' && <InfrastructureDatabasePanel />}
                        {activeSection === 'webapp' && <InfrastructureWebappPanel />}
                        {activeSection === 'cloud' && (
                            <Card>
                                <Card.Body className="text-center py-5">
                                    <i className="bi bi-cloud-fill display-4 text-muted mb-3 d-block"></i>
                                    <p className="text-muted">This section is under construction.</p>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <Row className="mb-4">
                <Col>
                    <h2><i className="bi bi-hdd-rack me-2"></i>Infrastructure Status</h2>
                    <p className="text-muted">Select a component to view its detailed status.</p>
                </Col>
            </Row>
            <Row className="g-4 justify-content-center">
                {SECTIONS.map(({ key, label, icon, description }) => (
                    <Col key={key} xs={12} sm={6} xl={3}>
                        <Card
                            className="h-100 text-center border-0 shadow-sm"
                            style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                            onClick={() => setActiveSection(key)}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 .5rem 1.5rem rgba(0,0,0,.15)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                        >
                            <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5 px-3">
                                <i className={`bi ${icon} display-3 text-primary mb-3`}></i>
                                <h5 className="fw-semibold mb-2">{label}</h5>
                                <p className="text-muted small mb-3">{description}</p>
                                <Button variant="primary" size="sm">
                                    View Details <i className="bi bi-arrow-right ms-1"></i>
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
        </Container>
    );
};

export default InfrastructureStatus;
